
import { Component, Prop } from "vue-property-decorator";
import Vue from "vue";
import TradingBotStats from "@/models/TradingBotStats";
import * as DateUtils from "@/utils/dateUtils";

/**
 * 📄 The possible bot statuses
 */
enum BotStatus {
  Active = "Active",
  Archived = "Archived",
  SettingUp = "Setting Up",
  MarketScanFailed = "Market Scan Failed",
  PotentialIssues = "Potential Issues",
}

@Component
export default class BotStatusIndicator extends Vue {
  @Prop()
  public botStats!: TradingBotStats;

  // 📦 The status of the selected bot
  private get botStatus(): BotStatus {
    if (this.botStats.enabled) {
      const now = new Date().getTime() / 1000;
      const candleStickSizeInSeconds = this.getSecondsFromCandleStickSize(
        this.botStats.candles
      );

      if (this.botStats.last_trade_attempt == null) {
        return BotStatus.SettingUp;
      }

      if (this.botStats.last_trade_attempt < now - 4 * candleStickSizeInSeconds) {
        return BotStatus.MarketScanFailed;
      }

      if (
        this.botStats.last_trade_attempt >= now - 4 * candleStickSizeInSeconds &&
        this.botStats.last_trade_attempt <= now
      ) {
        return BotStatus.Active;
      }
      return BotStatus.PotentialIssues;
    }
    return BotStatus.Archived;
  }

  // 🎨 The color of the chip's text
  private get statusChipTextColor(): string {
    switch (this.botStatus) {
      case BotStatus.Active:
        return "green--text font-weight-medium";
      case BotStatus.Archived:
        return "grey--text";
      case BotStatus.SettingUp:
        return "darkYellow--text";
      case BotStatus.MarketScanFailed:
        return "red--text";
      case BotStatus.PotentialIssues:
        return "darkYellow--text";
    }
  }

  // 📦 The color of the small circle indicator in front the bot status
  private get statusCircleIndicatorColor(): string {
    switch (this.botStatus) {
      case BotStatus.Active:
        return "green";
      case BotStatus.Archived:
        return "grey";
      case BotStatus.SettingUp:
        return "darkYellow";
      case BotStatus.MarketScanFailed:
        return "red";
      case BotStatus.PotentialIssues:
        return "darkYellow";
    }
  }

  // 📦 The text that should be displayed as the status
  private get statusDisplayText(): string {
    switch (this.botStatus) {
      case BotStatus.Active:
        return (
          "Active for " +
          DateUtils.formatSecondsToMinutesHoursOrDays(
            new Date().getTime() / 1000 - this.botStats.start_time
          )
        );
      case BotStatus.Archived:
        return (
          "Archived for " +
          DateUtils.formatSecondsToMinutesHoursOrDays(
            new Date().getTime() / 1000 - this.botStats.stop_time
          )
        );
      case BotStatus.SettingUp:
        return "Setting Up";
      case BotStatus.MarketScanFailed:
        return "Market Scan Failed";
      case BotStatus.PotentialIssues:
        return "Potential Issues";
    }
  }

  /**
   * 🔨 Parses the candlestick size and converts it to seconds
   */
  private getSecondsFromCandleStickSize(candleStickSize: string): number {
    const candlesSize: number = Number(candleStickSize.match(/\d+/)!![0]);
    const candlesUnit: string = candleStickSize.match(/[a-zA-Z]+/g)!![0];
    switch (candlesUnit) {
      case "h":
        return candlesSize * 3600;
      case "m":
        return candlesSize * 60;
      default:
        return -1;
    }
  }
}
